import { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navigation from "../Components/Navigation";
import RequireLoginComponent from "../Components/RequireLoginComponent";
import Api from "../Services/Api";
import { Col, Container, Row, Table, Spinner, Modal } from "react-bootstrap";
import { StoplightColor, lowerIsBetter, higherIsBetter, rangeColor } from "../Helpers/StoplightColor";
import DashboardLegendModal from "../Components/DashboardComponents/DashboardLegendModal";

type ProgramDashboardRow = Record<string, unknown>;

const COLUMN_DEFS = [
  {
    id: "activity" as const,
    label: "Activity",
    adherenceSnake: "weekly_activity_adherence",
    adherenceCamel: "weeklyActivityAdherence",
    importanceSnake: "percentage_activity_importance",
    importanceCamel: "percentageActivityImportance",
    redThreshold: 5,
    // steps/day: red < 4000, yellow < 7000, green >= 7000
    avgThresholds: { redBelow: 4000, yellowBelow: 7000 },
  },
  {
    id: "sleep" as const,
    label: "Sleep",
    adherenceSnake: "weekly_sleep_adherence",
    adherenceCamel: "weeklySleepAdherence",
    importanceSnake: "percentage_sleep_importance",
    importanceCamel: "percentageSleepImportance",
    redThreshold: 5,
    // sleep minutes: red < 300 (5h) or > 600 (10h), yellow < 420 (7h) or > 540 (9h), green 420–540
    avgThresholds: { redBelow: 300, redAbove: 600, yellowBelow: 420, yellowAbove: 540 },
  },
  {
    id: "weight" as const,
    label: "Weight",
    adherenceSnake: "weekly_weight_adherence",
    adherenceCamel: "weeklyWeightAdherence",
    importanceSnake: "percentage_weight_importance",
    importanceCamel: "percentageWeightImportance",
    redThreshold: 1,
    // BMI: red < 18.5 or > 30, yellow > 25, green 18.5–25
    avgThresholds: { redBelow: 18.5, redAbove: 30, yellowAbove: 25 },
  },
  {
    id: "nutrition" as const,
    label: "Nutrition",
    adherenceSnake: "weekly_nutrition_adherence",
    adherenceCamel: "weeklyNutritionAdherence",
    importanceSnake: "percentage_nutrition_importance",
    importanceCamel: "percentageNutritionImportance",
    redThreshold: 15,
    // avg score: red < 1.5, yellow 1.5–2.5 (excl), green >= 2.5
    avgThresholds: { redBelow: 1.5, yellowBelow: 2.5 },
    featureFlag: "hasNutritionFeature",
  },
  {
    id: "glucose" as const,
    label: "Glucose",
    adherenceSnake: "weekly_glucose_adherence",
    adherenceCamel: "weeklyGlucoseAdherence",
    importanceSnake: "percentage_glucose_importance",
    importanceCamel: "percentageGlucoseImportance",
    redThreshold: 1,
    // mg/dL: red > 120, yellow >= 100, green < 100
    avgThresholds: { redAbove: 120, yellowMin: 100 },
    featureFlag: "hasGlucoseFeature",
  },
  {
    id: "bloodPressure" as const,
    label: "Blood pressure",
    adherenceSnake: "weekly_blood_pressure_adherence",
    adherenceCamel: "weeklyBloodPressureAdherence",
    importanceSnake: "percentage_blood_pressure_importance",
    importanceCamel: "percentageBloodPressureImportance",
    redThreshold: 1,
    // mmHg: red sys > 140 or dia > 90, yellow sys >= 120 or dia >= 80, green sys < 120 and dia < 80
    avgThresholds: { redSysAbove: 140, redDiaAbove: 90, yellowSysMin: 120, yellowDiaMin: 80 },
    featureFlag: "hasBloodPressureFeature",
  },
];

function pickNumber(row: ProgramDashboardRow, snake: string, camel: string): number | null {
  const raw = row[snake] ?? row[camel];
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function pickFeatureFlag(row: ProgramDashboardRow, flag: string): boolean {
  return row[flag] === true;
}

function formatPercent(value: number | null): string {
  if (value === null) return "—";
  return `${Math.round(value)}%`;
}

function formatAdherenceFraction(value: number | null, denominator: number): string {
  if (value === null) return "—";
  const numerator = Math.ceil((value * denominator) / 100);
  return `${numerator}/${denominator}`;
}

function minutesToHHMM(minutes: number | null): string {
  if (minutes === null) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

type ColDef = (typeof COLUMN_DEFS)[number];

function getExtraInfo(
  row: ProgramDashboardRow,
  col: ColDef
): { extra: string | null; extraColor: StoplightColor | null } {
  if (col.id === "activity") {
    const { redBelow, yellowBelow } = col.avgThresholds;
    const steps = pickNumber(row, "step_count", "stepCount");
    if (steps === null) return { extra: null, extraColor: null };
    return { extra: `${Math.round(steps).toLocaleString()} steps`, extraColor: lowerIsBetter(steps, redBelow, yellowBelow) };
  }

  if (col.id === "sleep") {
    const { redBelow, redAbove, yellowBelow, yellowAbove } = col.avgThresholds;
    const mins = pickNumber(row, "sleep_time_minutes", "sleepTimeMinutes");
    if (mins === null) return { extra: null, extraColor: null };
    return { extra: minutesToHHMM(mins), extraColor: rangeColor(mins, redBelow, redAbove, yellowBelow, yellowAbove) };
  }

  if (col.id === "weight") {
    const { redBelow, redAbove, yellowAbove } = col.avgThresholds;
    const bmi = pickNumber(row, "bmi", "bmi");
    if (bmi === null) return { extra: null, extraColor: null };
    // no yellow zone on the low end, so yellowBelow = redBelow collapses it
    return { extra: `BMI ${bmi.toFixed(1)}`, extraColor: rangeColor(bmi, redBelow, redAbove, redBelow, yellowAbove) };
  }

  if (col.id === "nutrition") {
    const { redBelow, yellowBelow } = col.avgThresholds;
    const avg = pickNumber(row, "nutrition_average", "nutritionAverage");
    if (avg === null) return { extra: null, extraColor: null };
    return { extra: `Avg ${avg.toFixed(1)}`, extraColor: lowerIsBetter(avg, redBelow, yellowBelow) };
  }

  if (col.id === "glucose") {
    const { redAbove, yellowMin } = col.avgThresholds;
    const avg = pickNumber(row, "glucose_average", "glucoseAverage");
    if (avg === null) return { extra: null, extraColor: null };
    return { extra: `Avg ${avg.toFixed(1)}`, extraColor: higherIsBetter(avg, redAbove, yellowMin) };
  }

  if (col.id === "bloodPressure") {
    // BP takes two independent values — worst of the two wins, no single-value helper fits
    const { redSysAbove, redDiaAbove, yellowSysMin, yellowDiaMin } = col.avgThresholds;
    const sys = pickNumber(row, "systolic_blood_pressure_average", "systolicBloodPressureAverage");
    const dia = pickNumber(row, "diastolic_blood_pressure_average", "diastolicBloodPressureAverage");
    if (sys === null || dia === null) return { extra: null, extraColor: null };
    let extraColor: StoplightColor;
    if (sys > redSysAbove || dia > redDiaAbove) extraColor = "text-danger";
    else if (sys >= yellowSysMin || dia >= yellowDiaMin) extraColor = "text-warning";
    else extraColor = "text-success";
    return { extra: `Avg ${Math.round(sys)}/${Math.round(dia)}`, extraColor };
  }

  return { extra: null, extraColor: null };
}

function countOffTrack(row: ProgramDashboardRow): number {
  return COLUMN_DEFS.reduce((count, col) => {
    if ("featureFlag" in col && !pickFeatureFlag(row, col.featureFlag as string)) return count;
    const denominator = col.id === "nutrition" ? 21 : 7;
    const adherence = pickNumber(row, col.adherenceSnake, col.adherenceCamel);
    const numerator = adherence !== null ? Math.ceil((adherence * denominator) / 100) : null;
    const adherenceRed = numerator !== null && numerator <= col.redThreshold;
    const { extraColor } = getExtraInfo(row, col);
    return count + (adherenceRed || extraColor === "text-danger" ? 1 : 0);
  }, 0);
}

const MetricCell = ({
  adherence,
  importance,
  extra,
  extraColor,
  denominator,
  redThreshold,
  disabled = false,
}: {
  adherence: number | null;
  importance: number | null;
  extra?: string | null;
  extraColor?: StoplightColor | null;
  denominator: number;
  redThreshold: number;
  disabled?: boolean;
}) => {
  if (disabled) {
    return <div style={{ backgroundColor: "#f0f0f0", minHeight: "3rem" }} />;
  }

  const numerator = adherence !== null ? Math.ceil((adherence * denominator) / 100) : null;
  const isRed = numerator !== null && numerator <= redThreshold;
  return (
    <div className="text-center py-1 px-1">
      <div className={`fw-semibold small${isRed ? " text-danger" : ""}`}>
        {formatAdherenceFraction(adherence, denominator)}
      </div>
      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
        {importance !== null ? `${formatPercent(importance)} importance` : "—"}
      </div>
      {extra != null && (
        <div className={`fw-semibold ${extraColor ?? "text-muted"}`} style={{ fontSize: "0.75rem" }}>
          {extra}
        </div>
      )}
    </div>
  );
};

const VivantaProgramDashboard = () => {
  const { programId: id } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProgramDashboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);
  const [sortOrder, setSortOrder] = useState<"desc" | "asc" | null>(null);
  const [labModal, setLabModal] = useState<{ name: string; data: Record<string, unknown>[] | null } | null>(null);
  const [labLoading, setLabLoading] = useState(false);

  const openLabModal = async (row: ProgramDashboardRow) => {
    const name = (row["name"] ?? "—") as string;
    const externalId = (row["user"] as Record<string, unknown>)?.["externalId"] as string | undefined;
    if (!externalId) return;
    setLabModal({ name, data: null });
    setLabLoading(true);
    const api = new Api();
    const data = await api.getPatientHealthFairLabs(id!, externalId);
    setLabModal({ name, data: data ?? null });
    setLabLoading(false);
  };

  const displayedRows = sortOrder === null ? rows : [...rows].sort((a, b) => {
    const diff = countOffTrack(a) - countOffTrack(b);
    return sortOrder === "desc" ? -diff : diff;
  });

  useEffect(() => {
    window.rudderanalytics?.page("Program Dashboard");
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("staffToken");
    setIsNotAuthenticated(!token);
  }, []);

  useEffect(() => {
    if (isNotAuthenticated) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      const api = new Api();
      const results = await api.getProgramDashboard(id!);
      const rawData = results.sort(
        (a: any, b: any) => new Date(b.riskScoreDate).getTime() - new Date(a.riskScoreDate).getTime()
      );
      const data: any[] = [];
      for (const item of rawData) {
        if (!data.find((d: any) => d.user.id === item.user.id)) {
          data.push(item);
        }
      }
      console.log('data, pick the "latest date"', rawData)
      setRows(Object.values(data));
      setLoading(false);
    })();
  }, [isNotAuthenticated, id]);

  return (
    <Fragment>
      <Navigation />
      <Container className="mt-4 mb-5">
        <RequireLoginComponent isNotAuthenticated={isNotAuthenticated} />
        {!isNotAuthenticated && (
          <>
            <Row className="mb-3">
              <Col>
                <h1 className="d-flex align-items-center">
                  Program dashboard
                </h1>
                <p className="text-muted mb-0">
                  Each row is one register. Cells show weekly adherence (top) and importance (bottom).
                  <DashboardLegendModal />
                </p>
              </Col>
            </Row>
            {loading ? (
              <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status" />
              </div>
            ) : rows.length === 0 ? (
              <p className="text-muted">No dashboard data.</p>
            ) : (
              <Table responsive bordered hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    {COLUMN_DEFS.map((col) => (
                      <th key={col.id} className="text-center">
                        {col.label}
                      </th>
                    ))}
                    <th>Morbidities</th>
                    <th>Medications</th>
                    <th
                      className="text-center"
                      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                      onClick={() => setSortOrder(s => s === "desc" ? "asc" : s === "asc" ? null : "desc")}
                    >
                      Alerts {sortOrder === "desc" ? "↓" : sortOrder === "asc" ? "↑" : "↕"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td style={{ minWidth: "160px" }}>
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() => openLabModal(row)}
                          >
                            {(row["name"] ?? "—") as string}
                          </span>
                          <span
                            title="Open clinical history"
                            style={{ cursor: "pointer", fontSize: "0.85rem" }}
                            onClick={() => {
                              const user = row["user"] as Record<string, unknown> | undefined;
                              const externalId = user?.["externalId"] as string | undefined;
                              const userId = user?.["id"];
                              if (!externalId) return;
                              const params = new URLSearchParams();
                              if (row["name"]) params.set("name", row["name"] as string);
                              if (userId != null) params.set("userId", String(userId));
                              navigate(`/programs/${id}/clinical-history/${externalId}?${params}`);
                            }}
                          >
                            🔗
                          </span>
                        </div>
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {[
                            row["age"] != null ? `${row["age"]} años` : null,
                            row["latestWaistCm"] != null ? `${(row["latestWaistCm"] as number).toFixed(0)} cm` : null,
                            (row["phone"] as string | null) ?? null,
                          ].filter(Boolean).join(" · ")}
                        </div>
                      </td>
                      {COLUMN_DEFS.map((col) => {
                        const { extra, extraColor } = getExtraInfo(row, col);
                        const disabled = "featureFlag" in col ? !pickFeatureFlag(row, col.featureFlag as string) : false;
                        return (
                          <td key={col.id} className="p-0">
                            <MetricCell
                              adherence={pickNumber(row, col.adherenceSnake, col.adherenceCamel)}
                              importance={pickNumber(row, col.importanceSnake, col.importanceCamel)}
                              extra={extra}
                              extraColor={extraColor}
                              denominator={col.id === "nutrition" ? 21 : 7}
                              redThreshold={col.redThreshold}
                              disabled={disabled}
                            />
                          </td>
                        );
                      })}
                      <td style={{ minWidth: "160px", fontSize: "0.8rem" }}>
                        {(() => {
                          const morbidities = row["morbidities"] as Array<{ morbidity: string; modifiedByVivantaUserId?: number | null; cie10?: { name: string; code?: string | null } }> | undefined;
                          const bmi = pickNumber(row, "bmi", "bmi");
                          const bmiLabel =
                            bmi === null ? null :
                            bmi >= 40 ? "Obesity III" :
                            bmi >= 35 ? "Obesity II" :
                            bmi >= 30 ? "Obesity I" :
                            bmi >= 25 ? "Overweight" :
                            null;
                          const hasMorbidities = morbidities?.length || bmiLabel;
                          if (!hasMorbidities) return <span className="text-muted">—</span>;
                          return (
                            <>
                              {morbidities?.map((m, i) => (
                                <div key={i}>
                                  {m.morbidity}
                                  {m.modifiedByVivantaUserId != null && (
                                    <span className="text-muted ms-1">(by Vivanta)</span>
                                  )}
                                </div>
                              ))}
                              {bmiLabel && (
                                <div className="text-warning fw-semibold">{bmiLabel} <span className="text-muted fw-normal">(BMI {bmi!.toFixed(1)})</span></div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td style={{ minWidth: "160px", fontSize: "0.8rem" }}>
                        {(() => {
                          const treatments = row["activeTreatments"] as Array<{ name: string; medication?: { name: string } }> | undefined;
                          if (!treatments?.length) return <span className="text-muted">—</span>;
                          return treatments.map((t, i) => (
                            <div key={i}>
                              {t.medication?.name ?? t.name}
                              {t.medication && t.medication.name !== t.name && (
                                <span className="text-muted ms-1">({t.name})</span>
                              )}
                            </div>
                          ));
                        })()}
                      </td>
                      <td className="text-center fw-semibold">
                        {countOffTrack(row) > 0
                          ? <span className="text-danger">{countOffTrack(row)}</span>
                          : <span className="text-success">0</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Container>

      <Modal show={!!labModal} onHide={() => setLabModal(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{labModal?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {labLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : labModal?.data?.length ? (() => {
            const records = labModal.data!;
            const fields: Array<{ label: string; key: string; unit?: string; format?: (v: unknown) => string }> = [
              { label: "Heart rate", key: "heartRate", unit: "bpm" },
              { label: "Systolic BP", key: "systolic", unit: "mmHg" },
              { label: "Diastolic BP", key: "diastolic", unit: "mmHg" },
              { label: "Height", key: "heightCm", unit: "cm" },
              { label: "Weight", key: "weightKg", unit: "kg" },
              { label: "IMC", key: "imc" },
              { label: "HbA1c", key: "hba1c", unit: "%" },
              { label: "Triglycerides", key: "triglycerides", unit: "mg/dL" },
              { label: "HDL", key: "hdl", unit: "mg/dL" },
              { label: "LDL", key: "ldl", unit: "mg/dL" },
              { label: "Waist", key: "waistCm", unit: "cm" },
            ];
            return (
              <Table bordered size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th></th>
                    {records.map((r, i) => (
                      <th key={i} className="text-center">
                        {r["date"] ? new Date(r["date"] as string).toLocaleDateString() : `Visit ${i + 1}`}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fields.map(({ label, key, unit, format }) => (
                    <tr key={key}>
                      <td className="fw-semibold text-muted">{label}</td>
                      {records.map((r, i) => {
                        const val = r[key];
                        const display = format ? format(val) : val != null ? `${val}${unit ? ` ${unit}` : ""}` : "—";
                        return <td key={i} className="text-center">{display}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            );
          })() : (
            <p className="text-muted mb-0">No lab data available.</p>
          )}
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

export default VivantaProgramDashboard;
